/**
 * ENG-7405 - real MCP connectivity probe client (local STDIO).
 *
 * The stdio sibling of `probeMcpHttp`. Spawns the toolkit's bundled MCP server
 * exactly as the agent's `.mcp.json` entry would (same command / args / env),
 * runs `initialize -> notifications/initialized -> tools/list`, optionally
 * `tools/call` the toolkit's read-only `connectivity_test` tool, then tears the
 * child down. Maps the result to a {@link ConnectivityProbeOutcome}.
 *
 * Why spawn a throwaway instead of talking to the live session server: Claude
 * Code owns the live child's stdio pipes; the manager can't reach them. A fresh
 * spawn with the agent's real env is the honest host-side reachability check -
 * for broker-identity servers (origami) it even exercises the credential-fetch
 * path end to end, so a dead key / unreachable vendor surfaces as `down` rather
 * than the `builtin` rubber-stamp this replaces.
 *
 * MCP stdio framing is newline-delimited JSON-RPC (one message per line). We
 * hand-roll it (no MCP SDK dep in apps/cli), mirroring probeMcpHttp's
 * hand-rolled HTTP JSON-RPC. Read-only: initialize + tools/list + an optional
 * read-only tools/call.
 */
import { spawn } from 'node:child_process';
import type { ConnectivityProbeOutcome, ConnectivityTestOverride } from '@augmented/core/integrations';
import { handshakeToolsListOutcome } from '@augmented/core/integrations';

const DEFAULT_TIMEOUT_MS = 15_000;

export interface McpStdioProbeConfig {
  command: string;
  args?: string[];
  /** Resolved env for the child (the `.mcp.json` entry env, `${VAR}`-substituted). */
  env?: Record<string, string>;
  cwd?: string;
  /** Optional read-only `connectivity_test` tool to call after tools/list. */
  connectivityTest?: ConnectivityTestOverride | null;
  timeoutMs?: number;
}

interface RpcResult {
  result?: unknown;
  error?: { message?: string };
}

/**
 * Spawn the server, exchange the JSON-RPC messages, resolve the outcome. All
 * process/stream side effects are contained here so the mapping is exercised
 * against a real child in the unit test (a tiny fixture MCP server).
 */
export async function probeMcpStdio(config: McpStdioProbeConfig): Promise<ConnectivityProbeOutcome> {
  const timeoutMs = config.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const child = spawn(config.command, config.args ?? [], {
    // Faithful reproduction of the real spawn: the child inherits the manager's
    // process env (PATH/HOME so `node`/`npx` resolve) with the entry's env
    // overlaid, exactly as Claude Code spawns it.
    env: { ...process.env, ...(config.env ?? {}) },
    cwd: config.cwd,
    stdio: ['pipe', 'pipe', 'pipe'],
  });

  let stdout = '';
  let stderrTail = '';
  const pending = new Map<number, (rpc: RpcResult) => void>();

  return await new Promise<ConnectivityProbeOutcome>((resolve) => {
    let done = false;
    const finish = (outcome: ConnectivityProbeOutcome) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      child.stdout.removeAllListeners();
      try {
        child.kill('SIGKILL');
      } catch {
        /* already gone */
      }
      resolve(outcome);
    };

    // ENG-10574 — cause on this arm, and why it is only here.
    //
    // A cause is set only where the branch already KNOWS why, never inferred
    // from prose (ConnectivityProbeOutcome.cause: "guessing a cause at an
    // escalation boundary is how ENG-8355 / ENG-6428 happened"). On a stdio
    // server that is exactly one branch: this timeout. Everything else below is
    // deliberately left uncaused, which the API reads as `unknown` - still
    // actionable, still escalating:
    //
    //   - spawn failure (ENOENT/EACCES): graded `down`, and `not_run` would
    //     contradict that - `not_run` means "nothing was observed". Whether this
    //     branch SHOULD be `unverified` + `not_run` is a real open question: the
    //     child inherits the MANAGER's process.env, and ENG-8902 was precisely a
    //     manager-vs-agent PATH difference making the probe unable to find a
    //     binary the agent could. Flagged on ENG-10574, not changed here.
    //   - exit before answering: the stderr tail is prose. Unknown is honest.
    //   - JSON-RPC error on initialize / tools/list / tools/call: `RpcResult`
    //     does not carry the error code, only the message, so there is nothing
    //     machine-readable to classify.
    //   - tools/call `isError`: `semantic` looks tempting and is wrong. The
    //     branch's own comment says it covers "dead key / vendor down", so
    //     `semantic` would steer a dead credential away from a reconnect.
    //   - string[] connectivity_test args: our own seed misconfiguration. No
    //     cause in the vocabulary describes that.
    const timer = setTimeout(() => {
      finish({
        status: 'transient_error',
        // "network failure, DNS, or our own timeout" - the same call cli-probe.ts
        // makes for its own child-process timeout (ENG-10529). Not vendor-side,
        // so escalation is unchanged; the row just says why now.
        cause: 'unreachable',
        message: `MCP stdio handshake timed out after ${timeoutMs / 1000}s`,
      });
    }, timeoutMs);
    // The probe owns the whole lifecycle; don't let the timer keep the loop alive.
    if (typeof timer.unref === 'function') timer.unref();

    child.on('error', (err) => {
      // spawn failure (ENOENT, EACCES): the server can't even start - a real down.
      finish({ status: 'down', message: `MCP server failed to spawn: ${err.message}` });
    });
    child.on('exit', (code) => {
      // Exited before answering: dead server. Surface stderr tail for diagnosis.
      if (!done) {
        finish({
          status: 'down',
          message: `MCP server exited (code ${code ?? 'null'})${stderrTail ? `: ${stderrTail.trim().slice(-200)}` : ''}`,
        });
      }
    });
    // A write to a closed pipe (child exited mid-handshake) emits an async
    // `EPIPE` on stdin; without a listener that's an unhandled 'error' that
    // crashes the probe instead of returning an outcome. Swallow it - the real
    // failure already surfaces via the 'exit'/'error'/timeout paths as `down`.
    child.stdin.on('error', () => {});
    child.stderr.on('data', (d: Buffer) => {
      stderrTail = (stderrTail + d.toString()).slice(-500);
    });
    child.stdout.on('data', (d: Buffer) => {
      stdout += d.toString();
      let nl: number;
      while ((nl = stdout.indexOf('\n')) >= 0) {
        const line = stdout.slice(0, nl).trim();
        stdout = stdout.slice(nl + 1);
        if (!line) continue;
        let msg: (RpcResult & { id?: unknown }) | null = null;
        try {
          msg = JSON.parse(line) as RpcResult & { id?: unknown };
        } catch {
          continue; // non-JSON server log line on stdout - ignore
        }
        if (msg && typeof msg.id === 'number' && pending.has(msg.id)) {
          const cb = pending.get(msg.id)!;
          pending.delete(msg.id);
          cb(msg);
        }
      }
    });

    const send = (msg: Record<string, unknown>) => child.stdin.write(`${JSON.stringify(msg)}\n`);
    const request = (id: number, method: string, params?: Record<string, unknown>): Promise<RpcResult> =>
      new Promise((res) => {
        pending.set(id, res);
        send({ jsonrpc: '2.0', id, method, ...(params ? { params } : {}) });
      });

    void (async () => {
      const init = await request(1, 'initialize', {
        protocolVersion: '2025-03-26',
        capabilities: {},
        clientInfo: { name: 'augmented-connectivity-probe', version: '1.0.0' },
      });
      if (done) return;
      if (init.error) return finish({ status: 'down', message: `MCP initialize error: ${init.error.message ?? 'unknown'}` });

      send({ jsonrpc: '2.0', method: 'notifications/initialized' });

      const list = await request(2, 'tools/list');
      if (done) return;
      if (list.error) return finish({ status: 'down', message: `MCP tools/list error: ${list.error.message ?? 'unknown'}` });
      const toolCount = Array.isArray((list.result as { tools?: unknown[] })?.tools)
        ? (list.result as { tools: unknown[] }).tools.length
        : undefined;

      const testTool = config.connectivityTest?.tool;
      if (testTool) {
        const rawArgs = config.connectivityTest?.args;
        // An MCP tools/call takes an object. A string[] (the cli_tool `args`
        // form) is misconfigured metadata - fail loud rather than silently
        // calling with `{}`, so a bad seed can't read as a real green.
        if (Array.isArray(rawArgs)) {
          return finish({
            status: 'down',
            message: `connectivity_test.args for ${testTool} must be an object (got a string[])`,
          });
        }
        const toolArgs = rawArgs ?? {};
        const call = await request(3, 'tools/call', { name: testTool, arguments: toolArgs });
        if (done) return;
        if (call.error) return finish({ status: 'down', message: `MCP tools/call ${testTool} error: ${call.error.message ?? 'unknown'}` });
        // Tool-level failure: the call resolved but the tool reported isError -
        // a real "can't execute" (dead key / vendor down), not a green.
        if ((call.result as { isError?: boolean })?.isError === true) {
          return finish({ status: 'down', message: `MCP tool ${testTool} returned an error result` });
        }
        return finish({
          status: 'ok',
          message: `${testTool} succeeded`,
          details: { ...(toolCount !== undefined ? { toolCount } : {}), testTool },
        });
      }

      // ENG-8358: a zero-tool manifest is NOT a green (see handshakeToolsListOutcome).
      finish(handshakeToolsListOutcome(toolCount));
    })();
  });
}
